/**
 * Pure `buildMonsterSnapshot`: turns the DB monster row + its assigned
 * monster_moves (joined with the moves catalog) into the combat-core
 * MonsterSnapshot the simulator eats. Client uses this before a stage,
 * the server settle path uses the exact same function so the seed +
 * snapshot fed to `simulateStage` are byte-identical on both sides.
 *
 * v2 rule (matches Etappe-3 plan): every stage starts with full HP,
 * so hp = hpMax regardless of the row's current hp field. This keeps
 * snapshots hash-stable without needing to freeze last-settled hp.
 */

import type { MonsterSnapshot, MoveSnapshot, StatusFlag } from "@/lib/game/combat-core/types";
import type { CoreType } from "@/lib/game/combat-core/types";
import { castMsForMove, effectiveCooldownMs } from "@/lib/game/combat-core/cast";

const CORE_TYPES = new Set<CoreType>(["neon", "void", "plasma", "data", "glitch"]);

/**
 * Die Spalten, die `buildMonsterSnapshot` braucht — EINE Liste fuer alle Leser.
 *
 * ⚠️ Am 07.09.2026 ist genau hier ein Kampf auseinandergelaufen: Der Client las
 * `type` mit, der Settle nicht. Seit der Typvorteil an der ATTACKE haengt und
 * nicht mehr am Xenomon („Weg 2", 07.09.2026), trug damit jede Spieler-Attacke
 * auf dem Server den Typ ihres Traegers statt ihren eigenen — in jeder Zone
 * ohne `ignore_type_matrix` (also ab Zone 2) ein anderer Schaden, ein anderer
 * Kampf und im gemeldeten Fall ein anderer AUSGANG: Server „win", Client
 * „loss".
 *
 * Wer hier eine Spalte ergaenzt, ergaenzt sie fuer beide Seiten gleichzeitig.
 * Das ist der Zweck dieser Konstante.
 */
export const PLAYER_MOVE_SELECT =
  "slot, move:moves(id, name, description, type, power, cooldown_seconds, is_ultimate, effect)";

/**
 * Intrinsischer Grundangriff (Totzonen-Fix, 13.07.2026): Jedes Monster trägt
 * einen eingebauten Basis-Angriff mit NIEDRIGSTER Auto-Cast-Priorität (letzte
 * Listenposition — `pickAutoMove` nimmt den ersten bereiten Move). Er feuert
 * also nur, wenn kein regulärer Move bereit ist, und begrenzt damit die
 * Angriffslücke nach dem Eröffnungs-Burst auf max. 2,5s (vorher 8,7s, weil
 * alle Cooldowns synchron bei 0 starten). Nicht slot-belegbar, nicht
 * tauschbar — bewusst KEIN Katalog-Move, sonst wäre der Cooldown über die
 * Slot-Belegung exploitbar. Kalibrierter Sweep (scripts/deadzone-c-sweep.ts):
 * Zone-2-S8 FirstTry-Winrate 7% → ~51%, Zone 1 unverändert 100%.
 * Zugleich das einzige Sicherheitsnetz: ein Monster ohne Katalog-Moves
 * (frisch erzeugt, alles vergessen) kämpft allein damit — es gibt keinen
 * synthetischen Slot-1-Ersatz mehr.
 */
export const INTRINSIC_BASIC_ID = "__grundangriff";
export const INTRINSIC_BASIC_POWER = 0.4;
export const INTRINSIC_BASIC_COOLDOWN_MS = 2500;

/**
 * Anzeige-Name des Basis-Angriffs je Core-Typ — er soll sich wie eine echte
 * erste Attacke des Xenomons lesen, nicht wie ein Systemplatzhalter. Reiner
 * Anzeige-Wert: `finalizeStage` hasht keine Move-Felder, und der Core liest
 * `name` nirgends. Die ID bleibt `INTRINSIC_BASIC_ID`, daran hängen UI und
 * Balance-Sweeps.
 */
export const INTRINSIC_BASIC_NAMES: Record<CoreType, string> = {
  neon: "Neon-Impuls",
  void: "Leere-Riss",
  plasma: "Plasma-Stoß",
  data: "Daten-Spike",
  glitch: "Glitch-Stich",
};
export const INTRINSIC_BASIC_FALLBACK_NAME = "Grundangriff";

export function intrinsicBasicName(type: string | null | undefined): string {
  if (type && CORE_TYPES.has(type as CoreType)) return INTRINSIC_BASIC_NAMES[type as CoreType];
  return INTRINSIC_BASIC_FALLBACK_NAME;
}

export type MonsterRow = {
  id: string;
  level: number | null;
  atk: number | null;
  def: number | null;
  spd: number | null;
  hp_max: number | null;
  type: string | null;
  image_url?: string | null;
  name?: string | null;
};

export type MoveRowLite = {
  slot: number;
  move: {
    id: string;
    power: number | string | null;
    cooldown_seconds: number | null;
    is_ultimate?: boolean | null;
    effect?: unknown;
    name?: string | null;
    description?: string | null;
    type?: string | null;
  };
};

/**
 * Which catalogue effect means which combat-core status.
 *
 * The catalogue grew instead of being designed, so one idea carries several
 * names: `acc_down` sits next to `accuracy_down`, `spd_down` next to
 * `speed_down`. Every spelling that means the same status is listed here, so a
 * move no longer does nothing merely because its row spells the effect
 * differently than its twin. A kind that is NOT listed stays a pure damage
 * move — that is the deliberate fallback, not an oversight.
 */
const STATUS_BY_EFFECT_KIND: Record<string, StatusFlag> = {
  dot: "burn",
  accuracy_down: "blind",
  acc_down: "blind",
  def_down: "corrupt",
  spd_down: "lag",
  speed_down: "lag",
  slow: "lag",
  lag: "lag",
  stun: "disrupt",
};

/**
 * The numbers live here, not in the catalogue.
 *
 * Most rows carry no usable magnitude at all — a `def_down` payload is
 * `{kind, status, target}`, without a single number — and the DoT rows use two
 * incompatible shapes (`{dmg, duration_s}` on a handful, `{power, ticks}` on
 * the rest). So a default per status is the only honest source, and the data
 * may override it where it really carries one.
 *
 * `corrupt` at 0.15 is the value the retired idle combat used, so the defence
 * debuff keeps the strength it had when it last worked.
 */
const STATUS_DEFAULTS: Record<StatusFlag, { chance: number; magnitude: number; durationS: number }> = {
  burn: { chance: 0.85, magnitude: 3, durationS: 3 },
  blind: { chance: 0.85, magnitude: 0.3, durationS: 3 },
  corrupt: { chance: 0.85, magnitude: 0.15, durationS: 6 },
  lag: { chance: 0.85, magnitude: 0.2, durationS: 6 },
  atk_up: { chance: 1, magnitude: 0.25, durationS: 8 },
  def_up: { chance: 1, magnitude: 0.25, durationS: 8 },
  // Applied but inert: only a move with `interrupts` clears a running cast
  // (since 07.09.2026, superseding C7-A) — never this status, and no core module
  // reads `disrupt`. Kept only so the catalogue's `stun` rows map somewhere.
  disrupt: { chance: 0.7, magnitude: 1, durationS: 3 },
  // Welle 2 (27.09.2026): Beide kommen im Katalog nicht vor und werden hier nie
  // aus einer Zeile gelesen — der Spielkern legt sie selbst an (Sog als Anteil
  // des Treffers, Taumeln nach einer gelungenen Unterbrechung). Die Einträge
  // stehen nur, weil die Tabelle jeden Zustand kennen muss.
  sog: { chance: 1, magnitude: 0, durationS: 8 },
  taumeln: { chance: 1, magnitude: 1, durationS: 8 },
};

/**
 * Was eine Katalog-Zeile im Kampf bedeutet.
 *
 * Zwei Formate werden gelesen, und das ist Absicht:
 *
 *  - **Das entworfene Format** (seit 07.09.2026): `{kind:"damage", hits,
 *    lifesteal, interrupt, status:[{kind, chance, magnitude, durationS}]}`.
 *    Alles steht ausdrücklich da, mehrere Wirkungen sind möglich.
 *  - **Das alte, gewachsene Format**: eine einzelne `kind`-Angabe, aus der
 *    höchstens ein Status wird, mit Vorgabewerten für alles Übrige.
 *
 * Beide Wege existieren, damit die REIHENFOLGE von Veröffentlichung und
 * Datenbank-Umstellung egal ist. Läge nur das neue Format hier, müsste die SQL
 * exakt zwischen zwei Deploys laufen — und genau solche Reihenfolge-Zwänge
 * haben in diesem Projekt schon einmal Schaden angerichtet (CLAUDE.md §5).
 */
type Wirkungen = {
  applies?: MoveSnapshot["applies"];
  hits?: number;
  lifesteal?: number;
  interrupts?: boolean;
  heal?: number;
};

/** Status-Namen des entworfenen Formats auf Kernel-Status. */
const STATUS_BY_CANON: Record<string, StatusFlag> = {
  dot: "burn",
  acc_down: "blind",
  def_down: "corrupt",
  slow: "lag",
  atk_up: "atk_up",
  def_up: "def_up",
};

/** Die beiden Buffs liegen immer auf dem Träger, nie auf dem Ziel. */
const SELBST: ReadonlySet<StatusFlag> = new Set<StatusFlag>(["atk_up", "def_up"]);

export function extractWirkungen(effect: unknown): Wirkungen {
  if (!effect || typeof effect !== "object") return {};
  const e = effect as Record<string, unknown>;

  // --- Entworfenes Format -------------------------------------------------
  if (
    Array.isArray(e.status) ||
    typeof e.hits === "number" ||
    typeof e.lifesteal === "number" ||
    typeof e.heal === "number" ||
    e.interrupt === true
  ) {
    const applies: NonNullable<MoveSnapshot["applies"]> = [];
    for (const roh of Array.isArray(e.status) ? e.status : []) {
      if (!roh || typeof roh !== "object") continue;
      const st = roh as Record<string, unknown>;
      const kind = typeof st.kind === "string" ? STATUS_BY_CANON[st.kind] : undefined;
      if (!kind) continue;
      applies.push({
        kind,
        chance: typeof st.chance === "number" ? st.chance : 0.85,
        magnitude: typeof st.magnitude === "number" ? st.magnitude : STATUS_DEFAULTS[kind].magnitude,
        durationMs: (typeof st.durationS === "number" ? st.durationS : STATUS_DEFAULTS[kind].durationS) * 1000,
        ...(SELBST.has(kind) ? { on: "self" as const } : {}),
      });
    }
    return {
      applies: applies.length > 0 ? applies : undefined,
      hits: typeof e.hits === "number" ? e.hits : undefined,
      lifesteal: typeof e.lifesteal === "number" ? e.lifesteal : undefined,
      interrupts: e.interrupt === true ? true : undefined,
      heal: typeof e.heal === "number" ? e.heal : undefined,
    };
  }

  // --- Altes Format -------------------------------------------------------
  const kind = typeof e.kind === "string" ? e.kind : null;
  const status = kind ? STATUS_BY_EFFECT_KIND[kind] : undefined;
  if (!status) return {};
  const defaults = STATUS_DEFAULTS[status];
  const durationS = typeof e.duration_s === "number" ? e.duration_s : defaults.durationS;
  const magnitude = status === "burn" && typeof e.dmg === "number" ? e.dmg : defaults.magnitude;
  return {
    applies: [{ kind: status, chance: defaults.chance, magnitude, durationMs: durationS * 1000 }],
  };
}

function asCoreType(v: string | null | undefined): CoreType {
  if (v && CORE_TYPES.has(v as CoreType)) return v as CoreType;
  return "neon";
}

export function buildMonsterSnapshot(mon: MonsterRow, moves: MoveRowLite[]): MonsterSnapshot {
  const sorted = [...moves].sort((a, b) => a.slot - b.slot);
  const mapped: MoveSnapshot[] = sorted.map((m) => {
    const powerNum = typeof m.move.power === "number" ? m.move.power : Number(m.move.power ?? 1);
    const power = Number.isFinite(powerNum) ? powerNum : 1;
    const cdSeconds = m.move.cooldown_seconds ?? 30;
    const cdMs = Math.max(500, cdSeconds * 1000);
    const isUltimate = m.move.is_ultimate ?? false;
    // Cast-Zeit aus der Power abgeleitet (cast.ts) — dieselbe Regel wie für
    // Gegner-Moves. Nur bis zur Kopplungsgrenze (`CAST_CD_COUPLED_MAX_MS`) frisst sie den Cooldown;
    // dort bleibt der Angriffstakt erhalten und der Treffer fällt nur später
    // im Zyklus. Über 20s (lange Specials, Ultimates) kommt das Ausholen VOLL
    // OBENDRAUF, der Zyklus wächst dort um castMs.
    const castMs = castMsForMove({ power, isUltimate });
    const cooldownMs = effectiveCooldownMs(cdMs, castMs);
    return {
      id: m.move.id,
      power,
      cooldownMs,
      castMs,
      slot1: m.slot === 1,
      ...extractWirkungen(m.move.effect),
      name: m.move.name ?? undefined,
      description: m.move.description ?? undefined,
      moveType: m.move.type ?? null,
      // Anzeige-Spiegel von cooldownMs (Vertrag in types.ts) — also der
      // Cooldown NACH dem Treffer, nicht der rohe Katalog-Wert.
      cooldownSeconds: cooldownMs / 1000,
      isUltimate,
    };
  });

  // Intrinsischer Grundangriff — immer letzte Position (siehe Konstanten oben).
  mapped.push({
    id: INTRINSIC_BASIC_ID,
    name: intrinsicBasicName(mon.type),
    power: INTRINSIC_BASIC_POWER,
    cooldownMs: INTRINSIC_BASIC_COOLDOWN_MS,
    // Keine Cast-Zeit: der Grundangriff bleibt die Sofort-Reaktion, damit der
    // Totzonen-Fix exakt erhalten bleibt. Bewusst NICHT über castMsForMove —
    // sein Power-Band würde ihm 5s geben und die 2,5s-Taktung zerstören.
    castMs: 0,
    slot1: false,
    isIntrinsic: true,
    cooldownSeconds: INTRINSIC_BASIC_COOLDOWN_MS / 1000,
  });

  const hpMax = mon.hp_max ?? 100;
  return {
    id: mon.id,
    level: mon.level ?? 1,
    atk: mon.atk ?? 10,
    def: mon.def ?? 10,
    spd: mon.spd ?? 10,
    hp: hpMax, // v2: always full HP at stage start
    hpMax,
    type: asCoreType(mon.type),
    aspect: null,
    moves: mapped,
    comboStacks: 0,
    statusFlags: [] as { kind: StatusFlag; remainingMs: number; magnitude: number }[],
    imageUrl: mon.image_url ?? null,
    displayName: mon.name ?? null,
  };
}
